import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, CheckCircle, Anchor, AlertCircle, Link as LinkIcon } from "lucide-react";
import toast from "react-hot-toast";
import { CONFIG } from "../../config";
import api from "../../api/axios";
import SEO from "../../components/SEO";
import { useLanguage } from "../../context/LanguageContext";
import tracker from "../../utils/sanctuary-tracker";

export default function PublicApplication() {
    const [step, setStep] = useState("INTRO"); // INTRO, QUIZ, FORM, SUCCESS, REJECT
    const { t, language } = useLanguage();
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(false);

    // Quiz State
    const [currentQIndex, setCurrentQIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [score, setScore] = useState(0);

    // Local state for text input and selection
    const [textAnswer, setTextAnswer] = useState("");
    const [selectedOption, setSelectedOption] = useState(null);

    // Form State
    const [formData, setFormData] = useState({
        name: "",
        email: ""
    });

    // Fetch Questions on Mount
    useEffect(() => {
        const fetchQ = async () => {
            try {
                const res = await api.get("/gatekeeper/questions");
                setQuestions(res.data);
            } catch (err) {
                console.error(err);
            }
        };
        fetchQ();
    }, []);

    // --- QUIZ LOGIC ---
    const getWordCount = (text) => {
        if (!text) return 0;
        return text.trim().split(/\s+/).length;
    };
    const handleAnswer = (val) => {
        const finalVal = val || selectedOption;
        if (!finalVal) return; // Prevent empty answers

        // Word Limit Check for text answers
        if (getWordCount(finalVal) > 300) {
            return toast.error("Answer is too long. Please keep it under 300 words.");
        }

        // Save Answer
        setAnswers(prev => ({ ...prev, [questions[currentQIndex].text]: finalVal }));

        // Dynamic Score Logic
        const q = questions[currentQIndex];
        const points = q.points || 10; // Default to 10 if not set

        if (q.type === "MULTIPLE_CHOICE" && q.options?.indexOf(finalVal) === q.correctOption) {
            setScore(prev => prev + points);
        } else {
            // Partial credit or linear logic could go here, for now 0 or half?
            // Let's stick to strict scoring for "Correct" option, or full points for everything else if no correct option defined.
            if (q.correctOption === undefined || q.correctOption === null) {
                setScore(prev => prev + points); // Survey question = full points
            }
        }

        // Clear text input if valid
        setTextAnswer("");

        // Next or Finish
        if (currentQIndex < questions.length - 1) {
            setCurrentQIndex(prev => prev + 1);
        } else {
            tracker.track('guide_quiz_complete', { score });
            setStep("FORM");
        }
    };
    
    const handleBack = () => {
        if (step === "FORM") {
            setStep("QUIZ");
            setCurrentQIndex(questions.length - 1);
            const lastQ = questions[questions.length - 1];
            if (lastQ.type === "TEXT") setTextAnswer(answers[lastQ.text] || "");
            else setSelectedOption(answers[lastQ.text] || null);
        } else if (step === "QUIZ") {
            if (currentQIndex > 0) {
                const prevIdx = currentQIndex - 1;
                setCurrentQIndex(prevIdx);
                const prevQ = questions[prevIdx];
                if (prevQ.type === "TEXT") setTextAnswer(answers[prevQ.text] || "");
                else setSelectedOption(answers[prevQ.text] || null);
            } else {
                setStep("INTRO");
            }
        }
    };

    // --- SUBMIT LOGIC ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.email) return toast.error("Please fill all fields");

        setLoading(true);
        try {
            const res = await api.post("/gatekeeper/apply", {
                ...formData,
                answers,
                score,
                language // Using the destructured language variable
            });

            const data = res.data;

            tracker.track('guide_application_submitted');
            setStep("SUCCESS");

        } catch (err) {
            const errorMsg = err.response?.data?.error || err.message || "Submission failed";
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#fafaf9] dark:bg-gray-950 font-body flex items-center justify-center p-6">
            <SEO 
              title="Apply as a Guide" 
              description="Walk the Guide's path. Apply to become a listener and witness in the Antaryatra sanctuary." 
              url="/apply" 
            />
            <AnimatePresence mode="wait">

                {/* STEP 1: INTRO */}
                {step === "INTRO" && (
                    <motion.div
                        key="intro"
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
                        className="max-w-xl w-full text-center space-y-8"
                    >
                        <div className="w-20 h-20 mx-auto bg-brand-orange/10 rounded-full flex items-center justify-center text-brand-orange">
                            <Anchor className="w-10 h-10" />
                        </div>
                        <h1 className="text-4xl font-display font-medium text-gray-900 dark:text-gray-100">
                            The Circle of Ten
                        </h1>
                        <p className="text-[10px] uppercase tracking-[0.4em] text-brand-orange font-bold">
                            A Call from The All-seer
                        </p>
                        <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed italic font-serif">
                            "The All-seer is searching for the first 9 souls to join the original circle of ten. This is not a job; it is a sacred witnessing."
                        </p>
                        <p className="text-sm text-gray-400 dark:text-gray-500 leading-relaxed max-w-md mx-auto">
                            Antaryatra is not a marketplace; it is a sanctuary. Before you can enter the circle, you must walk through the mirror of truth.
                        </p>
                        <button
                            onClick={() => {
                                tracker.track('guide_apply_start');
                                setStep("QUIZ");
                            }}
                            className="px-8 py-4 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-full font-medium hover:scale-105 transition-transform"
                        >
                            {t('apply_begin_reflection')}
                        </button>
                    </motion.div>
                )}

                {/* STEP 2: QUIZ */}
                {step === "QUIZ" && (
                    questions.length > 0 ? (
                        <motion.div
                            key="quiz"
                            initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                            className="max-w-xl w-full bg-white dark:bg-gray-900 p-8 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-800"
                        >
                            <div className="mb-8 flex justify-between items-center text-xs font-bold text-gray-400 uppercase tracking-widest">
                                <span>Reflection {currentQIndex + 1}/{questions.length}</span>
                                <span>The Mirror</span>
                            </div>

                            <h2 className="text-2xl font-medium text-gray-900 dark:text-gray-100 mb-8">
                                {questions[currentQIndex]?.text}
                            </h2>

                            <div className="space-y-3">
                                {(questions[currentQIndex]?.type === "MULTIPLE_CHOICE" || questions[currentQIndex]?.type === "SCALE") ? (
                                    <div className="space-y-6">
                                        <div className="grid grid-cols-1 gap-3">
                                            {(questions[currentQIndex]?.options || ["1", "2", "3", "4", "5"]).map((opt, idx) => (
                                                <button
                                                    key={idx}
                                                    onClick={() => setSelectedOption(opt)}
                                                    className={`w-full text-left p-4 rounded-2xl border transition-all group flex justify-between items-center ${selectedOption === opt
                                                        ? "border-brand-blue bg-brand-blue/5 dark:bg-brand-blue/10 shadow-sm"
                                                        : "border-gray-100 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700"
                                                        }`}
                                                >
                                                    <span className={`${selectedOption === opt ? "text-brand-blue font-medium" : "text-gray-700 dark:text-gray-300"} group-hover:text-brand-blue transition-colors`}>{opt}</span>
                                                    <div className={`w-5 h-5 md:w-6 md:h-6 rounded-full border flex items-center justify-center transition-all ${selectedOption === opt
                                                        ? "border-brand-blue bg-brand-blue"
                                                        : "border-gray-200 dark:border-gray-700 group-hover:border-brand-blue"
                                                        }`}>
                                                        <div className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-white transition-opacity ${selectedOption === opt ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`} />
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <button
                                                onClick={handleBack}
                                                className="px-6 py-4 border border-gray-200 dark:border-gray-800 text-gray-500 dark:text-gray-400 rounded-2xl font-bold text-[10px] uppercase tracking-widest hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors whitespace-nowrap active:scale-95"
                                            >
                                                {t('apply_prev_step')}
                                            </button>
                                            <button
                                                onClick={() => handleAnswer(selectedOption)}
                                                disabled={!selectedOption}
                                                className="flex-1 py-4 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-2xl font-bold text-sm md:text-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-all flex items-center justify-center gap-3 shadow-xl shadow-gray-200 dark:shadow-none active:scale-95 disabled:opacity-50"
                                            >
                                                {currentQIndex === questions.length - 1 ? "Complete Reflection" : "Next Question"}
                                                <ArrowRight className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <textarea
                                            rows={8}
                                            className="w-full p-4 border rounded-2xl bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-800 focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue outline-none transition-all resize-none text-gray-800 dark:text-gray-200"
                                            placeholder="Type your reflection here... (Max 300 words)"
                                            value={textAnswer}
                                            onChange={(e) => setTextAnswer(e.target.value)}
                                        />
                                        <div className="flex items-center gap-4">
                                            <button
                                                onClick={handleBack}
                                                className="px-6 py-4 border border-gray-200 dark:border-gray-800 text-gray-500 dark:text-gray-400 rounded-2xl font-bold text-[10px] uppercase tracking-widest hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors whitespace-nowrap active:scale-95"
                                            >
                                                {t('apply_prev_step')}
                                            </button>
                                            <button
                                                onClick={() => handleAnswer(textAnswer)}
                                                disabled={!textAnswer || getWordCount(textAnswer) === 0}
                                                className="flex-1 py-4 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-2xl font-bold text-sm md:text-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-all flex items-center justify-center gap-3 shadow-xl shadow-gray-200 dark:shadow-none active:scale-95 disabled:opacity-50"
                                            >
                                                {currentQIndex === questions.length - 1 ? "Complete Reflection" : "Next Question"}
                                                <ArrowRight className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    ) : (
                        <div className="text-center p-8 bg-white dark:bg-gray-900 rounded-3xl shadow-xl border border-red-100">
                            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                            <h2 className="text-xl font-bold text-gray-900 mb-2">The Silence.</h2>
                            <p className="text-gray-600 mb-4">
                                There are no questions in the Mirror yet. <br />
                                This usually means the Admin hasn't set up the quiz.
                            </p>
                            <button
                                onClick={() => setStep("INTRO")}
                                className="text-brand-blue hover:underline"
                            >
                                Return to Gate
                            </button>
                        </div>
                    )
                )}

                {/* STEP 3: FORM */}
                {step === "FORM" && (
                    <motion.div
                        key="form"
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
                        className="max-w-md w-full bg-white dark:bg-gray-900 p-8 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-800"
                    >
                        <h2 className="text-2xl font-bold mb-2">Final Step</h2>
                        <p className="text-sm text-gray-500 mb-6">
                            Please share below details so we can contact you
                        </p>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Full Name</label>
                                <input
                                    required
                                    value={formData.name}
                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Email Address</label>
                                <input
                                    required
                                    type="email"
                                    value={formData.email}
                                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                                    className="w-full p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                                />
                            </div>

                            <button
                                disabled={loading}
                                className="w-full py-4 mt-4 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl font-medium hover:scale-105 transition-all shadow-xl"
                            >
                                {loading ? "Relaying Application..." : "Submit to the Council"}
                            </button>
                        </form>
                    </motion.div>
                )}

                {/* STEP 4: SUCCESS */}
                {step === "SUCCESS" && (
                    <motion.div
                        key="success"
                        initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                        className="max-w-md w-full text-center p-8 bg-white dark:bg-gray-900 rounded-3xl shadow-xl"
                    >
                        <div className="mx-auto w-20 h-20 bg-green-50 rounded-full flex items-center justify-center text-green-600 mb-6">
                            <CheckCircle className="w-10 h-10" />
                        </div>
                        <h2 className="text-2xl font-medium mb-2">{t('apply_success_title')}</h2>
                        <p className="text-gray-500 mb-6">
                            {t('apply_success_msg')}
                        </p>
                        <a href="/" className="text-brand-blue hover:underline text-sm font-medium">Return Home</a>
                    </motion.div>
                )}

            </AnimatePresence>
        </div>
    );
}
